import { LegalShell as Layout } from "@/components/connect/LegalShell";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/seo/SEOHead";
import { getCanonicalUrl, getBreadcrumbSchema } from "@/config/seo";
import { MARKETING_CONFIG } from "@/config/marketing";

/**
 * Legal Information page.
 *
 * Truthful posture: elefa is an Australian OTT VoIP service operated
 * by an Australian company. We are not yet registered with ACMA's CSP
 * register, not yet a TIO member, and have not appointed an EU/UK GDPR Art.27
 * representative, so this page must not imply otherwise. We must also not list
 * a "Data Protection Officer" as a formal appointed role.
 */
const LegalInformation = () => {
  const c = MARKETING_CONFIG.company;
  const hq = MARKETING_CONFIG.headquarters;

  return (
    <Layout>
      <SEOHead
        title="Legal Information · elefa"
        description="Legal entity, document framework and contact information for the elefa Voice-over-IP service."
        canonicalUrl={getCanonicalUrl('/legal')}
        structuredData={getBreadcrumbSchema([
          { name: 'Home', url: 'https://elefa.tel/' },
          { name: 'Legal Information', url: 'https://elefa.tel/legal' },
        ])}
      />
      <section className="py-16">
        <div className="container">
          <div className="mx-auto max-w-4xl">
            <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <span>/</span>
              <span className="text-foreground font-medium">Legal Information</span>
            </nav>

            <h1 className="text-4xl font-bold mb-2">Legal Information</h1>
            <p className="text-muted-foreground mb-8">
              Corporate and contact information for the elefa service.
            </p>

            <div className="prose prose-gray dark:prose-invert max-w-none space-y-8">

              <section>
                <h2 className="text-2xl font-semibold mb-4">1. The legal entity behind elefa</h2>
                <p className="text-muted-foreground">
                  The elefa application, websites and related services
                  are operated by:
                </p>
                <div className="mt-4 rounded-lg border bg-muted/30 p-5 text-sm">
                  <p><strong>{c.legalName}</strong></p>
                  <p>Trading as "elefa" (lowercase)</p>
                  <p>Australian Company Number (ACN): {c.acn}</p>
                  <p>Australian Business Number (ABN): {c.abn}</p>
                  <p>Country of incorporation: Australia</p>
                </div>
                <p className="text-muted-foreground mt-4">
                  References to "elefa", "we", "us" and "our" in any of our
                  legal documents mean {c.legalName} unless the context
                  requires otherwise. The word "<em>Connect</em>" sometimes
                  appears alongside the elefa name in marketing material; it
                  is a marketing label only and is not a separate legal
                  entity, brand owner or product.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">2. The Service</h2>
                <p className="text-muted-foreground">
                  elefa is a prepaid over-the-top (OTT) Voice-over-IP
                  application that lets you place low-rate calls from your
                  device to any phone number worldwide - including mobile
                  and landline numbers on phones with no internet connection
                  - over the public internet. It is not a regulated
                  telecommunications carriage service and cannot be used to
                  call emergency numbers such as 000, 112, 911, 999 or 111.
                  See Section 13 of our{" "}
                  <Link to="/legal/terms" className="text-primary hover:underline">Terms of Service</Link>
                  {" "}for the full safety notice and our{" "}
                  <Link to="/legal/service-availability" className="text-primary hover:underline">Service Availability</Link>
                  {" "}page for country-by-country guidance.
                </p>
                <p className="text-muted-foreground mt-4">
                  <strong>Not currently offered.</strong> In-app
                  elefa-to-elefa calling, messaging and collaboration
                  features are on our future roadmap and are not available
                  today. Any reference to them in our legal documents
                  describes how the Service <em>will</em> operate if and
                  when those features are released.
                </p>
              </section>


              <section>
                <h2 className="text-2xl font-semibold mb-4">3. Regulatory posture (truthful summary)</h2>
                <p className="text-muted-foreground">
                  elefa is established and regulated in Australia. We are an
                  early-stage OTT VoIP supplier and want to be straightforward
                  about what we have and have not done:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li>We are <strong>not a licensed telecommunications carrier</strong> and we are not currently listed on ACMA's Carriage Service Provider register. Our service profile as an OTT VoIP supplier does not require carrier licensing.</li>
                  <li>We are <strong>not currently a member of the Telecommunications Industry Ombudsman</strong> scheme. We will join when our service profile requires it.</li>
                  <li>We have <strong>not registered, certified or filed</strong> with any regulator outside Australia. We do not hold a UK ICO registration, an EU/UK GDPR Article 27 representative, a Kenya ODPC, Ghana DPA, Nigeria NDPC, or any other foreign data-protection or telecommunications registration.</li>
                  <li>We follow the Privacy Act 1988 (Cth) and the Australian Consumer Law because we are an Australian business. We do our best to respect the consumer-protection laws of the countries where our users live, but we are not established in those countries and only Australian law governs the contract.</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  See our{" "}
                  <Link to="/regulatory" className="text-primary hover:underline">Regulatory Information</Link>
                  {" "}page for the longer version and our{" "}
                  <Link to="/legal/service-availability" className="text-primary hover:underline">Service Availability</Link>
                  {" "}page for countries where local law restricts OTT VoIP.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">4. Document framework</h2>
                <p className="text-muted-foreground">
                  Your relationship with elefa is governed by the
                  following documents. Each is incorporated by reference into
                  the others. If there is an inconsistency between them, the
                  Terms of Service prevail unless a non-excludable consumer or
                  data-protection law requires otherwise.
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                  <li><Link to="/legal/terms" className="text-primary hover:underline">Terms of Service</Link></li>
                  <li><Link to="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link></li>
                  <li><Link to="/legal/cookies" className="text-primary hover:underline">Cookie Policy</Link></li>
                  <li><Link to="/legal/fair-use" className="text-primary hover:underline">Fair Use Policy</Link></li>
                  <li><Link to="/legal/complaints" className="text-primary hover:underline">Complaints Policy</Link></li>
                  <li><Link to="/legal/financial-hardship" className="text-primary hover:underline">Financial Difficulty Policy</Link></li>
                  <li><Link to="/legal/service-availability" className="text-primary hover:underline">Service Availability</Link></li>
                  <li><Link to="/regulatory" className="text-primary hover:underline">Regulatory Information</Link></li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">5. Trademarks and copyright</h2>
                <p className="text-muted-foreground">
                  "elefa" and the elefa logo are unregistered trademarks of
                  {" "}{c.legalName}. "Connect" used alongside the elefa
                  name is a marketing label, not a registered mark or
                  separate brand. All other trademarks referenced are the
                  property of their respective owners. The contents of our
                  applications and websites - including software, design,
                  copy, graphics and audio - are protected by copyright and
                  other intellectual-property laws and may not be reproduced
                  without our prior written consent except as permitted by law.
                </p>
              </section>


              <section>
                <h2 className="text-2xl font-semibold mb-4">6. Reporting unlawful content or abuse</h2>
                <p className="text-muted-foreground">
                  To report content or behaviour on the Service that you
                  believe is unlawful, fraudulent, abusive or harmful, email
                  <strong> security@elefa.com.au</strong>. To submit a copyright
                  takedown notice, email <strong>legal@elefa.com.au</strong>{" "}
                  with the information required by your local copyright law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">7. Lawful access requests</h2>
                <p className="text-muted-foreground">
                  Government and law-enforcement agencies seeking to compel
                  disclosure of customer information must contact{" "}
                  <strong>legal@elefa.com.au</strong> with appropriately-issued
                  Australian legal process (or its mutual-legal-assistance
                  equivalent for non-Australian agencies). We assess every
                  request for lawfulness, scope and specificity, and we
                  challenge requests that are overbroad or unlawful. In
                  verified emergencies involving an imminent risk to life we
                  will respond on an expedited basis under section 287 of the
                  Telecommunications Act 1997 (Cth).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold mb-4">8. Contact</h2>
                <div className="rounded-lg border bg-muted/30 p-5 text-sm">
                  <p><strong>Customer support:</strong> support@elefa.com.au</p>
                  <p><strong>Billing:</strong> billing@elefa.com.au</p>
                  <p><strong>Privacy and data requests:</strong> privacy@elefa.com.au</p>
                  <p><strong>Complaints:</strong> complaints@elefa.com.au</p>
                  <p><strong>Trust and safety / abuse:</strong> security@elefa.com.au</p>
                  <p><strong>Legal and law-enforcement:</strong> legal@elefa.com.au</p>
                  <p className="mt-3"><strong>Postal:</strong> {c.legalName}, {hq}</p>
                </div>
              </section>

            </div>

            <div className="mt-8 flex flex-wrap gap-4 text-sm">
              <Link to="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/legal/terms" className="text-primary hover:underline">Terms of Service</Link>
              <span className="text-muted-foreground">•</span>
              <Link to="/regulatory" className="text-primary hover:underline">Regulatory Information</Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LegalInformation;
